/**
 * product-catalog-data.ts — Pure data layer for the H2HUMANS product catalog.
 *
 * This file contains NO Vite-specific imports (no `@/` alias, no image assets).
 * It can be imported by:
 *   - Vite frontend code (src/lib/products.ts adds the image assets on top)
 *   - Supabase Edge Functions (Deno, via relative path)
 *   - Any other consumer that needs product/bundle data
 *
 * ⚠️ This is the SOURCE OF TRUTH for prices, SKUs, bundle composition, and
 *    Stripe price lookup keys. Everything else derives from this data.
 *
 * Change anything here and it propagates automatically everywhere.
 */

// ── Types ───────────────────────────────────────────────────────────────

export type ProductId = "body-care" | "hydration" | "balance";
export type CatalogId = ProductId | "bundle";

export interface LocalizedText {
  de: string;
  en: string;
  pt: string;
}

// ── SKU map ─────────────────────────────────────────────────────────────

export const SKU_MAP: Record<string, string> = {
  "body-care": "ZZ-H2H-BOD-PROT-1KG-01",
  hydration: "ZZ-H2H-HYD-ELEC-30ST-01",
  balance: "ZZ-H2H-BAL-MULTI-30C-01",
};

// ── Individual product prices (EUR, tax included) ───────────────────────

export const PRODUCT_PRICES: Record<ProductId, number> = {
  "body-care": 69.9,
  hydration: 29.9,
  balance: 19.9,
};

// ── Single-product quantity tiers ───────────────────────────────────────

/**
 * Price per unit of a single product by its quantity in the cart (EUR, tax
 * included): 1 = list price · 2 = about −5 % · 3 or more = about −10 %.
 * Used by the cart UI AND recomputed server-side in create-checkout.
 */
export const PRODUCT_TIER_PRICES: Record<ProductId, readonly [number, number, number]> = {
  "body-care": [69.9, 66.4, 62.9],
  hydration: [29.9, 28.4, 26.9],
  balance: [19.9, 18.9, 17.9],
};

/** Advertised saving per tier (index = quantity − 1). */
export const PRODUCT_TIER_PERCENT = [0, 5, 10] as const;

/** Package cards on single product pages (quantity = pieces). */
export const PRODUCT_PACKAGES = [
  { quantity: 1, badge: null },
  { quantity: 2, badge: "popular" },
  { quantity: 3, badge: "best" },
] as const;

const tierIndex = (quantity: number): number =>
  Math.min(Math.max(1, Math.floor(Number.isFinite(quantity) ? quantity : 1)), 3) - 1;

export const isProductId = (id: string): id is ProductId => id in PRODUCT_PRICES;

export function getProductUnitPrice(id: ProductId, quantity: number): number {
  return PRODUCT_TIER_PRICES[id][tierIndex(quantity)];
}

export interface SingleLinePricing {
  /** List price per unit. */
  base: number;
  /** Final line total after the winning discount (EUR). */
  lineTotal: number;
  /** Final price per unit (lineTotal / quantity, rounded to cents). */
  unit: number;
  /** Discount amount of the line (EUR). */
  discount: number;
  /** Which discount is active on this line. Never both. */
  source: "tier" | "code" | null;
  /** Percent shown to the customer for the active discount. */
  percent: number;
}

/**
 * Quantity discount and promo code are NOT combinable: per line the higher
 * discount wins (lower line total). Code discount is rounded per line.
 */
export function priceSingleLine(id: ProductId, quantity: number, codePercent = 0): SingleLinePricing {
  const q = Math.max(1, Math.floor(Number.isFinite(quantity) ? quantity : 1));
  const baseCents = Math.round(PRODUCT_PRICES[id] * 100);
  const fullCents = baseCents * q;
  const idx = tierIndex(q);
  const tierCents = Math.round(PRODUCT_TIER_PRICES[id][idx] * 100) * q;
  const pct = Math.max(0, Math.min(100, codePercent || 0));
  const codeCents = fullCents - Math.round((fullCents * pct) / 100);
  let lineCents = fullCents;
  let source: SingleLinePricing["source"] = null;
  let percent = 0;
  if (pct > 0 && codeCents < tierCents) {
    lineCents = codeCents; source = "code"; percent = pct;
  } else if (idx > 0) {
    lineCents = tierCents; source = "tier"; percent = PRODUCT_TIER_PERCENT[idx];
  }
  return {
    base: baseCents / 100,
    lineTotal: lineCents / 100,
    unit: Math.round(lineCents / q) / 100,
    discount: (fullCents - lineCents) / 100,
    source,
    percent,
  };
}

// ── Bundle composition ──────────────────────────────────────────────────

/** Quantities required of each product to form one bundle. */
export const BUNDLE_REQUIREMENTS: Record<ProductId, number> = {
  "body-care": 1,
  hydration: 1,
  balance: 1,
};

export const BUNDLE_PRODUCT_IDS = Object.keys(BUNDLE_REQUIREMENTS) as ProductId[];

/** Sum of individual product prices that make up one bundle. */
export const BUNDLE_INDIVIDUAL_TOTAL = BUNDLE_PRODUCT_IDS.reduce((sum, id) => {
  return sum + (PRODUCT_PRICES[id] ?? 0) * BUNDLE_REQUIREMENTS[id];
}, 0);

// ── Bundle tier pricing ─────────────────────────────────────────────────

/**
 * Price per set by quantity in the cart (EUR, tax included).
 * 1 set = 94,90 · 2 sets = 89,90 each · 3 or more = 84,90 each.
 * Used by the cart UI AND recomputed server-side in create-checkout.
 */
export const BUNDLE_TIER_PRICES = [94.9, 89.9, 84.9] as const;

export function getBundleUnitPrice(quantity: number): number {
  const q = Math.max(1, Math.floor(Number.isFinite(quantity) ? quantity : 1));
  return BUNDLE_TIER_PRICES[Math.min(q, BUNDLE_TIER_PRICES.length) - 1];
}

export const getBundleUnitPriceCents = (quantity: number): number =>
  Math.round(getBundleUnitPrice(quantity) * 100);

/** Package cards on the bundle page (months = sets). */
export const BUNDLE_PACKAGES = [
  { quantity: 1, badge: null },
  { quantity: 2, badge: "popular" },
  { quantity: 3, badge: "best" },
] as const;

/** Product included free in every set (shown as GRATIS). */
export const BUNDLE_FREE_PRODUCT_ID: ProductId = "balance";

// ── Bundle metadata ─────────────────────────────────────────────────────

export const BUNDLE_DATA = {
  id: "bundle" as const,
  name: {
    de: "Daily Routine Komplett-Set",
    en: "Daily Routine Komplett-Set",
    pt: "Daily Routine Komplett-Set",
  },
  /** Price of a single set (tier 1). Quantity-based prices via getBundleUnitPrice. */
  price: BUNDLE_TIER_PRICES[0] as number,
  /** Value of the individual products in one set (shown strikethrough). */
  msrp: BUNDLE_INDIVIDUAL_TOTAL,
};

// ── Stripe price lookup keys ────────────────────────────────────────────

/**
 * Map cart item id → Stripe price lookup_key.
 * Set via create_product / batch_create_product.
 * Keep this in sync with the Payments dashboard.
 */
export const STRIPE_PRICE_IDS: Record<CatalogId, string> = {
  "body-care": "body_care_onetime",
  hydration: "hydration_onetime",
  balance: "balance_onetime",
  bundle: "bundle_onetime",
};
// ── Product page content (shop redesign) ────────────────────────────────

/** Ingredient entry for product pages (amount per daily serving). */
export interface ProductPageIngredient {
  name: LocalizedText;
  amountPerServing: string;
  function: LocalizedText;
}

export interface ProductPageFaq {
  question: LocalizedText;
  answer: LocalizedText;
}

/**
 * Extra fields for the product pages. Extends the catalog above — it is
 * NOT a second product definition (name, price, image and Stripe key stay
 * where they are). Empty values = content still to be supplied.
 */
export interface ProductPageContent {
  slug: string;
  /** Unit price statement, e.g. "69,90 € / kg". */
  unitPrice: LocalizedText | null;
  benefitSentence: LocalizedText | null;
  benefitBullets: LocalizedText[];
  ingredients: ProductPageIngredient[];
  usage: LocalizedText | null;
  faq: ProductPageFaq[];
  phaseTexts: { before: LocalizedText | null; during: LocalizedText | null; after: LocalizedText | null };
  /** Core benefits for the icon bar. */
  keyBenefits: LocalizedText[];
  /** Net content in kg for the legally required unit price (€/kg); null = unknown. */
  netQuantityKg: number | null;
  /** Capsules per pack; shows a per-capsule price (Daily Balance). */
  unitsPerPack?: number;
}

const emptyPage = (slug: string): ProductPageContent => ({
  slug,
  unitPrice: null,
  benefitSentence: null,
  benefitBullets: [],
  ingredients: [],
  usage: null,
  faq: [],
  phaseTexts: { before: null, during: null, after: null },
  keyBenefits: [],
  netQuantityKg: null,
});

export const PRODUCT_PAGE_CONTENT: Record<CatalogId, ProductPageContent> = {
  bundle: emptyPage("supplement-system"),
  // 1000 g Dose (SKU …1KG).
  "body-care": { ...emptyPage("daily-body-care"), netQuantityKg: 1 },
  balance: { ...emptyPage("daily-balance"), unitsPerPack: 30 },
  // 30 Sticks à 5 g = 150 g.
  hydration: { ...emptyPage("daily-hydration"), netQuantityKg: 0.15 },
};

/** Display order on /shop. */
export const SHOP_ORDER: CatalogId[] = ["bundle", "body-care", "balance", "hydration"];

/** Price per capsule, rounded to cents (e.g. 19,90 € / 30 = 0,66 €). */
export const perUnitPrice = (packPrice: number, units: number): number => Math.round((packPrice / units) * 100) / 100;

export const getCatalogIdBySlug = (slug: string): CatalogId | undefined =>
  (Object.keys(PRODUCT_PAGE_CONTENT) as CatalogId[]).find((id) => PRODUCT_PAGE_CONTENT[id].slug === slug);

export const getProductPath = (id: CatalogId): string => `/produkte/${PRODUCT_PAGE_CONTENT[id].slug}`;
